import { useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, ExternalLink, FileUp, Pencil, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { formatDate } from '@/lib/format';
import {
  isLocked,
  useUpdateTestOrder,
} from '@/features/patient-record/hooks/useTestOrderMutations';
import type {
  TestOrderStatus,
  TestOrderViewModel,
} from '@/features/patient-record/types';

import { RecordResultDialog } from './RecordResultDialog';
import { TestOrderTimeline } from './TestOrderTimeline';
import { toTestOrderInput } from './toTestOrderInput';

const STATUS_OPTIONS: readonly TestOrderStatus[] = [
  'ORDERED',
  'SAMPLE_SENT',
  'RESULTED',
  'REVIEWED',
  'CANCELLED',
];

interface TestOrderExpandedProps {
  readonly guid: string;
  readonly order: TestOrderViewModel;
  readonly onEdit: () => void;
  readonly onCancel: () => void;
}

export function TestOrderExpanded({
  guid,
  order,
  onEdit,
  onCancel,
}: TestOrderExpandedProps): ReactElement {
  const { t } = useTranslation();
  const updateMutation = useUpdateTestOrder(guid);
  const locked = isLocked(order);
  const isCancelled = order.status === 'CANCELLED';
  const [recordingResult, setRecordingResult] = useState(false);

  // PUT replaces the full record, so the body is built off the current VM
  // and only the status differs. RESULTED is the exception: it needs a
  // result summary (or an external report URL) to be accepted, so it routes
  // through the result dialog rather than going straight out (PTV2-1563).
  const handleStatusChange = (next: TestOrderStatus): void => {
    if (next === order.status) return;
    if (next === 'RESULTED') {
      setRecordingResult(true);
      return;
    }
    updateMutation.mutate({
      id: order.id,
      input: toTestOrderInput(order, { status: next }),
    });
  };

  // RESULTED only follows SAMPLE_SENT on the BE state machine, so the button
  // is offered there — and again on a RESULTED order, so a report can be
  // backfilled or a summary corrected without reopening the edit dialog.
  const canRecordResult =
    !locked && (order.status === 'SAMPLE_SENT' || order.status === 'RESULTED');

  return (
    <div className="mt-2 ml-6 rounded-md bg-muted/40 border border-border/60 p-3 space-y-3">
      {!isCancelled && <TestOrderTimeline status={order.status} />}

      {isCancelled ? (
        /*
         * Cancelled is a terminal state — the BE rejects further status
         * transitions. Render the status read-only with a note instead
         * of a dropdown so the user doesn't try to "undo" a cancellation
         * from here. To revive the order, the user records a new one.
         */
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {t('patient.testOrders.statusFieldLabel')}:
            </span>
            <span className="text-xs font-medium text-card-foreground">
              {t('patient.testOrders.status.CANCELLED')}
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground italic">
            {t('patient.testOrders.cancelledStatusLockedHint')}
          </p>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <label
            className="text-xs text-muted-foreground"
            htmlFor={`status-${order.id}`}
          >
            {t('patient.testOrders.statusFieldLabel')}:
          </label>
          <Select
            id={`status-${order.id}`}
            value={order.status}
            disabled={updateMutation.isPending || locked}
            onChange={(event) => {
              handleStatusChange(event.target.value as TestOrderStatus);
            }}
            className="h-7 w-40 text-xs"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {t(`patient.testOrders.status.${option}`)}
              </option>
            ))}
          </Select>
          {/*
           * Spec: moving to REVIEWED is a deliberate clinician action.
           * Surface a one-click affordance only on RESULTED rows so the
           * common path (acknowledge a result) doesn't require the
           * dropdown.
           */}
          {order.status === 'RESULTED' && !locked && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 gap-1.5 text-xs"
              onClick={() => {
                handleStatusChange('REVIEWED');
              }}
              disabled={updateMutation.isPending}
            >
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
              {t('patient.testOrders.markReviewed')}
            </Button>
          )}
          {canRecordResult && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 gap-1.5 text-xs"
              onClick={() => {
                setRecordingResult(true);
              }}
              disabled={updateMutation.isPending}
            >
              <FileUp className="h-3.5 w-3.5" aria-hidden="true" />
              {order.status === 'RESULTED'
                ? t('patient.testOrders.result.updateTitle')
                : t('patient.testOrders.result.dialogTitle')}
            </Button>
          )}
        </div>
      )}

      {(order.testCode !== null || order.panelName !== null) && (
        <p className="text-xs text-muted-foreground">
          {order.panelName !== null && (
            <>
              {t('patient.testOrders.panelName')}: {order.panelName}
              {order.testCode !== null && ' · '}
            </>
          )}
          {order.testCode !== null && (
            <>
              {t('patient.testOrders.testCode')}: {order.testCode}
            </>
          )}
        </p>
      )}

      {order.indication !== null && (
        <Section label={t('patient.testOrders.clinicalIndication')}>
          <p className="rounded border border-border bg-card px-3 py-2 text-card-foreground whitespace-pre-wrap">
            {order.indication}
          </p>
        </Section>
      )}

      {(order.sampleType !== null ||
        order.specimen !== null ||
        order.sampleCollectedDate !== null ||
        order.sampleSentDate !== null) && (
        <Section label={t('patient.testOrders.sample')}>
          <ul className="text-xs text-muted-foreground space-y-0.5">
            {order.sampleType !== null && (
              <li>
                {t('patient.testOrders.sampleType')}:{' '}
                {t(`patient.testOrders.sampleTypeOption.${order.sampleType}`)}
              </li>
            )}
            {order.specimen !== null && (
              <li>
                {t('patient.testOrders.specimen')}: {order.specimen}
              </li>
            )}
            {order.sampleCollectedDate !== null && (
              <li>
                {t('patient.testOrders.sampleCollectedDate')}:{' '}
                {formatDate(order.sampleCollectedDate)}
              </li>
            )}
            {order.sampleSentDate !== null && (
              <li>
                {t('patient.testOrders.sampleSentDate')}:{' '}
                {formatDate(order.sampleSentDate)}
              </li>
            )}
          </ul>
        </Section>
      )}

      {(order.resultSummary !== null ||
        order.externalReportUrl !== null ||
        order.resultDate !== null) && (
        <Section label={t('patient.testOrders.results')}>
          {order.resultDate !== null && (
            <p className="text-xs text-muted-foreground">
              {t('patient.testOrders.resultDate')}: {formatDate(order.resultDate)}
            </p>
          )}
          {order.resultSummary !== null && (
            <p className="rounded border border-border bg-card px-3 py-2 text-card-foreground whitespace-pre-wrap">
              {order.resultSummary}
            </p>
          )}
          {order.externalReportUrl !== null && (
            <a
              href={order.externalReportUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              <ExternalLink className="h-3 w-3" aria-hidden="true" />
              {t('patient.testOrders.openExternalReport')}
            </a>
          )}
        </Section>
      )}

      {order.orderedByExternal !== null && (
        <p className="text-xs text-muted-foreground">
          {t('patient.testOrders.orderedByExternalLabel', {
            value: order.orderedByExternal,
          })}
        </p>
      )}

      {order.notes !== null && (
        <Section label={t('patient.testOrders.notes')}>
          <p className="rounded border border-border bg-card px-3 py-2 text-card-foreground whitespace-pre-wrap">
            {order.notes}
          </p>
        </Section>
      )}

      <div className="flex gap-2 pt-1">
        {!locked && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1 gap-1.5"
            onClick={onEdit}
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
            {t('patient.testOrders.editOrder')}
          </Button>
        )}
        {!isCancelled && !locked && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1 gap-1.5 text-destructive hover:text-destructive"
            onClick={onCancel}
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
            {t('patient.testOrders.cancelOrder')}
          </Button>
        )}
      </div>

      {recordingResult && (
        <RecordResultDialog
          guid={guid}
          order={order}
          onClose={() => {
            setRecordingResult(false);
          }}
        />
      )}
    </div>
  );
}

interface SectionProps {
  readonly label: string;
  readonly children: ReactNode;
}

function Section({ label, children }: SectionProps): ReactElement {
  return (
    <div className="space-y-1">
      <p className="text-xs font-semibold text-muted-foreground">{label}</p>
      <div className="text-xs">{children}</div>
    </div>
  );
}
